import { useEffect, useRef, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import baseURL from "../../config/api";
import Select from "react-select";
import Pagination from "../../components/Pagination";
import { formatUSPhone } from "../../utils/phoneFormatter";
import useGroup from "../../hook/useGroup";
import useConfirm from "../../hook/useConfirm";
import useAuth from "../../hook/useAuth";
import { MdEmail } from "react-icons/md";
import { BsFillTelephoneFill } from "react-icons/bs";
import Cookies from "js-cookie";

const PAGE_SIZE = 10;

const SubgroupMembers = () => {
    const token = Cookies.get("token-my-cdir");
    const { id } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const subGroup = location.state?.subGroup;
    const { auth }: any = useAuth();
    const { selectedGroupId }: any = useGroup();
    const { setConfirmPopup, setData: setConfirmData }: any = useConfirm();
    const [members, setMembers] = useState<any[]>([]);
    const [allMembers, setAllMembers] = useState<any[]>([]);
    const [familyOptions, setFamilyOptions] = useState<any[]>([]);
    const [selectedMember, setSelectedMember] = useState<any>(null);
    const [adding, setAdding] = useState(false);
    const [removingMap, setRemovingMap] = useState<any>({});
    const [loading, setLoading] = useState(false);
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        size: 10
    });
    const [totalLength, setTotalLength] = useState(0);
    const [showEntries, setShowEntries] = useState(10);
    const [optionPage, setOptionPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [loadingOptions, setLoadingOptions] = useState(false);
    const [searchValue, setSearchValue] = useState<string>("");
    const [churchName, setChurchName] = useState<string>("");

    const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        if (selectedGroupId) {
            fetch(
                `${baseURL}group/${selectedGroupId}`,
                {
                    headers: {                           
                        Authorization: `Bearer ${token?.toString()}`
                    }
                }
            )
                .then((res) => res.json())
                .then((data) => {
                    setChurchName(data?.name);
                })
                .catch((error) => {
                    console.log(error);
                    setChurchName("");
                });
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedGroupId])

    const fetchMembers = async () => {
        if (!selectedGroupId) return;

        setLoading(true);
        try {
            const res = await fetch(
                `${baseURL}FamilyMember/GetFamilyMemberReportByGroupIdAndSubGroupIdPaging/${id},${selectedGroupId},${paginationModel.page},${paginationModel.size}`, {
                headers: {
                    Authorization: `Bearer ${token?.toString()}`
                }
            }
            );
            const data = await res.json();

            const formattedMembers = (data?.familyMemberReport || []).map((item: any) => ({
                ...item,
                filePath: item?.filePath
                    ? `${baseURL}commonFile/Get?name=${item.filePath}`
                    : "/images/user-default.jpg",
            }));

            setMembers(formattedMembers);
            setTotalLength(data?.count || 0);
        } catch (err) {
            console.error(err);
            setMembers([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchMembers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paginationModel.page, paginationModel.size, selectedGroupId]);

    const fetchAllMembers = async () => {
        if (!selectedGroupId) return;

        let allData: any[] = [];
        let page = 0;
        const size = 100;

        try {
            while (true) {
                const res = await fetch(
                    `${baseURL}FamilyMember/GetFamilyMemberReportByGroupIdAndSubGroupIdPaging/${id},${selectedGroupId},${page},${size}`, {
                    headers: {
                        Authorization: `Bearer ${token?.toString()}`
                    }
                }
                );
                const data = await res.json();

                const batch = data?.familyMemberReport || [];
                if (page === 0) {
                    allData = batch;
                } else {
                    allData = [...allData, ...batch];
                }

                if (batch.length < size) break;
                page++;
            }

            setAllMembers(allData);
        } catch (err) {
            console.error(err);
        }
    };

    useEffect(() => {
        fetchAllMembers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedGroupId]);

    const fetchFamilyOptions = async (page = 0, name = "", replace = false) => {
        if (!selectedGroupId || loadingOptions) return;

        setLoadingOptions(true);

        try {
            let url = "";

            if (name?.trim() === "") {
                url = `${baseURL}FamilyMember/GetFamilyMemberReportSortedByNameAndByGroupId/${selectedGroupId},${page},${PAGE_SIZE}`;
            } else {
                url = `${baseURL}FamilyMember/GetFamilyMemberReportSortedByNameAndByGroupIdAndSearchWithMemberName?memberName=${name}&groupId=${selectedGroupId}&page=${page}&size=${PAGE_SIZE}`;
            }

            const res = await fetch(url, {
                headers: {
                    Authorization: `Bearer ${token?.toString()}`
                }
            });
            const data = await res.json();

            const newOptions = (data?.familyMemberReport || []).map((item: any) => ({
                value: item.id,
                label: `${item.firstName} ${item.lastName}`
            }));

            setFamilyOptions((prev) => {
                let updatedOptions;
                if (replace || page === 0) {
                    updatedOptions = newOptions;
                } else {
                    const existing = new Set(prev.map((p) => p.value));
                    const filtered = newOptions.filter((o: any) => !existing.has(o.value));
                    updatedOptions = [...prev, ...filtered];
                }

                // compute hasMore after merging
                const totalCount = data?.count || 0;
                setHasMore(updatedOptions.length < totalCount);

                return updatedOptions;
            });

            const totalCount = data?.count || 0;
            const currentLoaded = (replace || page === 0 ? 0 : familyOptions.length) + newOptions.length;
            setHasMore(currentLoaded < totalCount);
        } catch (err) {
            console.error(err);
        } finally {
            setLoadingOptions(false);
        }
    };

    useEffect(() => {
        setOptionPage(0);
        setFamilyOptions([]);
        setHasMore(true);
        fetchFamilyOptions(0, searchValue, true);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedGroupId]);

    const handleInputChange = (input: string, actionMeta: any) => {
        if (actionMeta.action === "input-change") {
            setSearchValue(input);

            if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);

            debounceTimeoutRef.current = setTimeout(() => {
                setOptionPage(0);
                setFamilyOptions([]);
                setHasMore(true);
                fetchFamilyOptions(0, input, true);
            }, 1000);
        }
    }

    const addMemberToSubGroup = async () => {
        if (!selectedMember) return;

        setAdding(true);

        try {

            const checkRes = await fetch(
                `${baseURL}FamilyMember/DoesFamilyMemberAlreadyExist/${selectedMember?.value},${id}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            if (!checkRes.ok) {
                throw new Error(
                    `Check member API failed: ${checkRes.status} ${checkRes.statusText}`
                );
            }

            const isExist = await checkRes.json();

            if (!isExist) {
                const createRes = await fetch(
                    `${baseURL}familyMemberSubGroup/CreateFamilyMemberSubGroups`,
                    {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${token}`,
                        },
                        body: JSON.stringify([
                            {
                                subGroupId: id,
                                familyMemberId: selectedMember.value,
                            },
                        ]),
                    }
                );

                if (!createRes.ok) {
                    const errorText = await createRes.text();

                    console.error("Create member error:", {
                        status: createRes.status,
                        statusText: createRes.statusText,
                        response: errorText,
                    });

                    throw new Error(
                        `Create member API failed: ${createRes.status}`
                    );
                }

                setSelectedMember(null);
                setSearchValue("");

                await fetchMembers();
                await fetchAllMembers();

                setOptionPage(0);
                setFamilyOptions([]);
                setHasMore(true);

                await fetchFamilyOptions(0, "", true);
            } else {
                setConfirmData({
                    title: "Warning",
                    summary: `${selectedMember?.label} already exists.`,
                    confirmBtn: "Ok",
                    isCancelBtn: true,
                    isConfirm: (cb: any) => {
                        cb();
                    },
                });

                setConfirmPopup(true);

                setSelectedMember(null);
                setSearchValue("");

                await fetchFamilyOptions(0, "", true);
            }
        } catch (err) {
            console.error("Add member error:", err);
        } finally {
            setAdding(false);
        }
    };

    const removeMemberFromSubGroup = async (memberId: number) => {
        const key = `${id}-${memberId}`;
        setRemovingMap((p: any) => ({ ...p, [key]: true }));

        try {
            await fetch(
                `${baseURL}familyMemberSubGroup/DeleteFamilyMemberSubGroupWithFamilyMemberIdAndSubGroupId/${memberId},${id},${auth?.id}`,
                {
                    method: "DELETE",
                    headers: {
                        Authorization: `Bearer ${token?.toString()}`
                    }
                }
            );

            setPaginationModel((prev) => ({
                ...prev,
                page: 0,
            }));

            await fetchMembers();
            fetchAllMembers();
            setSelectedMember(null);
            fetchFamilyOptions(0, searchValue, true);
        } finally {
            setRemovingMap((p: any) => ({ ...p, [key]: false }));
        }
    };

    const handlePrint = () => window.print();

    return (
        <>
            <section className="p-4 xm:p-6">
                <div className="flex flex-col xm:flex-row xm:justify-between gap-4 mb-6">
                    <h1 className="text-xl xm:text-2xl font-bold text-secondary">
                        {subGroup?.name || "SubGroup"} Members
                    </h1>

                    <div className="flex flex-col xm:flex-row gap-2 xm:gap-3 w-full xm:w-auto">
                        <button onClick={handlePrint} className="bg-green-500 text-white px-4 py-2 rounded w-full xm:w-auto">
                            Download PDF
                        </button>

                        <button onClick={() => navigate(-1)} className="bg-gray-400 text-white px-4 py-2 rounded w-full xm:w-auto">
                            Back
                        </button>
                    </div>
                </div>

                <div className="flex flex-col xm:flex-row gap-3 mb-6">
                    <div className="flex-1">
                        <Select
                            options={familyOptions}
                            value={selectedMember}
                            onChange={(val) => setSelectedMember(val)}
                            onInputChange={handleInputChange}
                            onMenuScrollToBottom={() => {
                                if (!loadingOptions && hasMore) {
                                    const nextPage = optionPage + 1;
                                    setOptionPage(nextPage);
                                    fetchFamilyOptions(nextPage, searchValue, false);
                                }
                            }}
                            isLoading={loadingOptions}
                            placeholder="Select Member..."
                            filterOption={null}
                            noOptionsMessage={() => {
                                if (loadingOptions) return "Loading...";
                                if (hasMore) return "Scroll to load more...";
                                return "No more members";
                            }}
                        />
                    </div>

                    <button
                        onClick={addMemberToSubGroup}
                        disabled={!selectedMember || adding}
                        className="bg-secondary text-white px-4 py-2 rounded"
                    >
                        {adding ? "Adding..." : "Add"}
                    </button>
                </div>

                {!loading && members.length > 0 ? (
                    <div className="grid grid-cols-1 xm:grid-cols-2 gap-4">
                        {members.map((member: any) => {
                            const key = `${id}-${member.id}`;

                            return (
                                <div
                                    key={member.id}
                                    className="flex items-center gap-6 border p-1.5 rounded-xl bg-white shadow-md min-h-[140px]"
                                >
                                    <img
                                        src={member.filePath}
                                        alt="member"
                                        className="w-full xm:w-[150px] xm:h-[150px] object-cover object-top"
                                        onError={({ currentTarget }) => {
                                            currentTarget.onerror = null;
                                            currentTarget.src = "/images/user-default.jpg";
                                        }}
                                    />

                                    <div className="flex-1">
                                        <p className="font-bold text-base">
                                            {member.firstName} {member.lastName}
                                        </p>

                                        <div className="flex items-center gap-2 mt-1">
                                            <MdEmail className="text-gray-500 shrink-0" size={16} />

                                            <p className="text-base text-gray-600 break-all">
                                                {member.emailAddress}
                                            </p>
                                        </div>

                                        <div className="flex items-center gap-2 mt-2">
                                            <BsFillTelephoneFill
                                                className="text-gray-500 shrink-0"
                                                size={14}
                                            />

                                            <p className="text-base text-gray-600">
                                                {formatUSPhone(member.phoneNumber)}
                                            </p>
                                        </div>

                                        <button
                                            className={`text-xs mt-2 ${removingMap[key]
                                                ? "text-gray-400"
                                                : "text-red-500"
                                                }`}
                                            disabled={removingMap[key]}
                                            onClick={() =>
                                                removeMemberFromSubGroup(member.id)
                                            }
                                        >
                                            {removingMap[key]
                                                ? "Removing..."
                                                : "Remove"}
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : !loading ? (
                    <div className="text-center text-gray-400 mt-10 no-print">
                        No members found
                    </div>
                ) : null}

                {totalLength > 0 && (
                    <div className="mt-6 flex flex-col xm:flex-row items-center justify-between gap-3">
                        <Pagination
                            totalLength={totalLength}
                            paginationModel={paginationModel}
                            setPaginationModel={setPaginationModel}
                            showEntries={showEntries}
                            setShowEntries={(val) => {
                                setShowEntries(val);
                                setPaginationModel({ page: 0, size: val });
                            }}
                        />
                    </div>
                )}
            </section>

            <div
                id="print-section"
                className="hidden print:block"
            >
                <div className="border-b-2 border-secondary pb-3 mb-5">
                    <div className="w-full flex justify-center">
                        <h1 className="text-lg font-bold text-secondary">
                            {churchName}
                        </h1>
                    </div>

                    <div className="flex justify-between items-center mt-2 text-xs text-gray-600">
                        <span className="font-semibold text-[0.875rem]">
                            {subGroup?.name} - Members
                        </span>

                        <span>
                            {new Date().toLocaleString("en-US", {
                                hour: "numeric",
                                minute: "numeric",
                                hour12: true,
                                day: "2-digit",
                                month: "2-digit",
                                year: "numeric",
                            })}
                        </span>
                    </div>
                </div>

                {allMembers.length === 0 ? (
                    <div className="text-center mt-10">No data available</div>
                ) : (
                    <table className="w-full border border-collapse text-sm">
                        <thead>
                            <tr className="bg-gray-200">
                                <th className="border p-2">#</th>
                                <th className="border p-2">Full Name</th>
                                <th className="border p-2">Email</th>
                                <th className="border p-2">Phone</th>
                            </tr>
                        </thead>

                        <tbody>
                            {allMembers.map((m, i) => (
                                <tr key={m.id}>
                                    <td className="border p-2">{i + 1}</td>
                                    <td className="border p-2">
                                        {m.firstName} {m.lastName}
                                    </td>
                                    <td className="border p-2">{m.emailAddress}</td>
                                    <td className="border p-2">
                                        {formatUSPhone(m.phoneNumber)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </>
    );
};

export default SubgroupMembers;